import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Input,
  Select,
  SelectItem,
  CircularProgress,
  Chip,
  Tooltip,
  Switch,
} from "@nextui-org/react";
import { BsTrash } from "react-icons/bs";
import { IoChevronDown, IoCloseSharp, IoInformationCircleOutline } from "react-icons/io5";
import { MdDragIndicator } from "react-icons/md";
import { HiSwitchVertical } from "react-icons/hi";
import { TbTestPipe } from "react-icons/tb";
import { LuGauge, LuPlus } from "react-icons/lu";
import { Reorder, useDragControls } from "framer-motion";
import { useTranslation } from "react-i18next";

import myAxios from "../../utils/axiosWrapper";

const CALL_DIRECTIONS = [
  { key: "all", label: "Tous les appels" },
  { key: "incoming", label: "Appels entrants" },
  { key: "outgoing", label: "Appels sortants" },
];

const WEIGHT_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-purple-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-orange-500",
  "bg-teal-500",
];

const EMPTY_RULE = {
  name: "",
  department_ids: [],
  call_direction: "all",
  components: [],
};

const getFieldTypeFromList = (gradableFields, fieldName) => {
  const field = gradableFields.find((f) => f.name === fieldName);
  return field?.type || "?";
};

const computeGrade = (components, demoValues, getFieldType) => {
  if (!components || components.length === 0) return null;

  const result = components.reduce(
    (acc, comp) => {
      // If trigger_field is set and trigger is falsy, skip this component
      if (comp.trigger_field) {
        const triggerVal = demoValues[comp.trigger_field];
        if (!triggerVal) return acc;
      }

      const rawValue = demoValues[comp.field_name];
      if (rawValue === undefined || rawValue === "") return acc;

      const fieldType = getFieldType(comp.field_name);
      let normalized;

      if (fieldType === "number") {
        normalized = parseFloat(rawValue) / 10;
      } else if (fieldType === "bool") {
        const boolVal = rawValue ? 1 : 0;
        normalized = comp.bool_inverted ? 1 - boolVal : boolVal;
      } else {
        return acc;
      }

      return {
        weightedSum: acc.weightedSum + normalized * comp.weight,
        totalWeight: acc.totalWeight + comp.weight,
      };
    },
    { weightedSum: 0, totalWeight: 0 }
  );

  if (result.totalWeight === 0) return null;
  return (result.weightedSum / result.totalWeight) * 10;
};

const GradeDemo = ({ components, getFieldType, t }) => {
  const [demoValues, setDemoValues] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  // Reset demo values when components change
  useEffect(() => {
    const initial = {};
    components.forEach((comp) => {
      const type = getFieldType(comp.field_name);
      if (type === "number") initial[comp.field_name] = "5";
      else if (type === "bool") initial[comp.field_name] = true;
      // Initialize trigger fields as true (active by default)
      if (comp.trigger_field && initial[comp.trigger_field] === undefined) {
        initial[comp.trigger_field] = true;
      }
    });
    setDemoValues(initial);
  }, [components]);

  const grade = useMemo(
    () => computeGrade(components, demoValues, getFieldType),
    [components, demoValues, getFieldType]
  );

  if (!components || components.length === 0) return null;

  const getGradeColor = (g) => {
    if (g === null) return "text-gray-400";
    if (g >= 8) return "text-emerald-600";
    if (g >= 5) return "text-amber-600";
    return "text-red-600";
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
      >
        <TbTestPipe size={16} />
        {isOpen ? t("Masquer la demo") : t("Tester la note")}
      </button>
      {isOpen && (
        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
          <div className="text-xs font-medium text-indigo-700 mb-2">{t("Simulateur de note")}</div>
          {(() => {
            const triggerFields = [
              ...new Set(components.filter((c) => c.trigger_field).map((c) => c.trigger_field)),
            ];
            return triggerFields.map((tf) => (
              <div
                key={`trigger-${tf}`}
                className="flex items-center gap-3 pb-1 border-b border-indigo-100"
              >
                <span className="text-xs text-indigo-600 w-36 truncate font-medium">
                  {tf.replace(/_/g, " ")}
                </span>
                <Switch
                  size="sm"
                  isSelected={demoValues[tf] ?? true}
                  onValueChange={(val) => setDemoValues({ ...demoValues, [tf]: val })}
                  color="secondary"
                />
                <span className="text-[10px] text-indigo-400">
                  {demoValues[tf] ? t("actif (condition)") : t("inactif (condition)")}
                </span>
              </div>
            ));
          })()}
          {components.map((comp) => {
            const fieldType = getFieldType(comp.field_name);
            const displayName = comp.field_name.replace(/_/g, " ");
            const isTriggered = comp.trigger_field ? demoValues[comp.trigger_field] ?? true : true;

            if (fieldType === "number") {
              return (
                <div
                  key={comp.field_name}
                  className={`flex items-center gap-3 ${!isTriggered ? "opacity-30" : ""}`}
                >
                  <span className="text-xs text-gray-600 w-36 truncate">{displayName}</span>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={demoValues[comp.field_name] ?? "5"}
                    onChange={(e) =>
                      setDemoValues({ ...demoValues, [comp.field_name]: e.target.value })
                    }
                    className="flex-1 h-2 accent-primary"
                    disabled={!isTriggered}
                  />
                  <span className="text-xs font-mono w-8 text-right text-gray-700">
                    {!isTriggered ? "N/A" : `${demoValues[comp.field_name] ?? "5"}/10`}
                  </span>
                </div>
              );
            }

            if (fieldType === "bool") {
              const isChecked = demoValues[comp.field_name] ?? true;
              return (
                <div
                  key={comp.field_name}
                  className={`flex items-center gap-3 ${!isTriggered ? "opacity-30" : ""}`}
                >
                  <span className="text-xs text-gray-600 w-36 truncate">{displayName}</span>
                  <Switch
                    size="sm"
                    isSelected={isChecked}
                    onValueChange={(val) =>
                      setDemoValues({ ...demoValues, [comp.field_name]: val })
                    }
                    isDisabled={!isTriggered}
                  />
                  <span className="text-xs text-gray-500">
                    {!isTriggered ? "N/A" : isChecked ? t("Oui") : t("Non")}
                    {isTriggered && comp.bool_inverted && (
                      <span className="text-amber-600 ml-1">({t("inverse")})</span>
                    )}
                  </span>
                </div>
              );
            }

            return null;
          })}
          <div className="flex items-center gap-3 pt-2 border-t border-indigo-200">
            <span className="text-sm font-medium text-gray-700">{t("Note calculee")}:</span>
            <span className={`text-2xl font-bold ${getGradeColor(grade)}`}>
              {grade !== null ? `${grade.toFixed(1)}/10` : "-"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const WeightDistributionBar = ({ components }) => {
  const totalWeight = components.reduce((sum, c) => sum + (c.weight || 0), 0);
  if (totalWeight === 0) return null;

  return (
    <div className="mt-2">
      <div className="flex rounded-lg overflow-hidden h-6">
        {components.map((comp, idx) => {
          const pct = ((comp.weight || 0) / totalWeight) * 100;
          if (pct === 0) return null;
          return (
            <Tooltip
              key={idx}
              content={`${comp.field_name}: ${Math.round(pct)}%`}
            >
              <div
                className={`${
                  WEIGHT_COLORS[idx % WEIGHT_COLORS.length]
                } flex items-center justify-center text-white text-[10px] font-medium min-w-[24px]`}
                style={{ width: `${pct}%` }}
              >
                {pct >= 15 ? `${Math.round(pct)}%` : ""}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const ReorderableRuleCard = ({ rule, index, t }) => {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={rule}
      dragListener={false}
      dragControls={dragControls}
      className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 cursor-default"
      whileDrag={{ scale: 1.02, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
    >
      <div
        onPointerDown={(e) => dragControls.start(e)}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
      >
        <MdDragIndicator size={24} />
      </div>
      <Chip
        size="sm"
        color="primary"
        variant="flat"
      >
        #{index + 1}
      </Chip>
      <div className="flex-1">
        <span className="font-medium text-sm">{rule.name}</span>
        <div className="flex gap-2 mt-1">
          {rule.department_ids?.length > 0 && (
            <Chip
              size="sm"
              variant="flat"
              color="warning"
            >
              {rule.department_ids.length} {t("dept(s)")}
            </Chip>
          )}
          {rule.call_direction !== "all" && (
            <Chip
              size="sm"
              variant="flat"
              color="secondary"
            >
              {t(CALL_DIRECTIONS.find((d) => d.key === rule.call_direction)?.label || "")}
            </Chip>
          )}
          <Chip
            size="sm"
            variant="flat"
          >
            {rule.components?.length || 0} {t("composant(s)")}
          </Chip>
        </div>
      </div>
    </Reorder.Item>
  );
};

// Editor for a rule's fields (name, scope, components). Used for existing rules
// and for the creation form.
const RuleForm = ({
  rule,
  onChange,
  isReadOnly,
  gradableFields,
  departmentOptions,
  errors = {},
  t,
}) => {
  const [componentField, setComponentField] = useState("");
  const [componentWeight, setComponentWeight] = useState("5");
  const [componentTrigger, setComponentTrigger] = useState("");

  const getFieldType = (fieldName) => getFieldTypeFromList(gradableFields, fieldName);

  const availableFields = gradableFields.filter(
    (f) => !(rule.components || []).some((c) => c.field_name === f.name)
  );
  const triggerFieldOptions = gradableFields.filter(
    (f) => f.type === "bool" && f.name !== componentField
  );

  const handleAddComponent = () => {
    const weight = parseInt(componentWeight, 10);
    if (!componentField || !weight || weight < 1 || weight > 10) return;
    if ((rule.components || []).some((c) => c.field_name === componentField)) return;

    const fieldType = getFieldType(componentField);
    const newComp = {
      field_name: componentField,
      weight,
      ...(fieldType === "bool" ? { bool_inverted: false } : {}),
      ...(componentTrigger ? { trigger_field: componentTrigger } : {}),
    };

    onChange({ components: [...(rule.components || []), newComp] });
    setComponentField("");
    setComponentWeight("5");
    setComponentTrigger("");
  };

  const handleRemoveComponent = (fieldName) => {
    onChange({ components: rule.components.filter((c) => c.field_name !== fieldName) });
  };

  const handleToggleBoolInverted = (fieldName) => {
    onChange({
      components: rule.components.map((c) =>
        c.field_name === fieldName ? { ...c, bool_inverted: !c.bool_inverted } : c
      ),
    });
  };

  return (
    <div className="space-y-3">
      <Input
        label={t("Nom de la regle")}
        placeholder={t("ex: Note SAV Entrant")}
        value={rule.name}
        onChange={(e) => onChange({ name: e.target.value })}
        isReadOnly={isReadOnly}
        variant={isReadOnly ? "flat" : "bordered"}
        isInvalid={!!errors.name}
        errorMessage={errors.name}
        classNames={{ input: "text-sm", label: "text-xs font-medium" }}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Select
            label={t("Departements")}
            placeholder={t("Tous les departements")}
            selectionMode="multiple"
            selectedKeys={new Set(rule.department_ids?.map(String) || [])}
            onSelectionChange={(keys) => {
              onChange({ department_ids: Array.from(keys).map((k) => k.toString()) });
            }}
            isDisabled={isReadOnly}
            variant={isReadOnly ? "flat" : "bordered"}
            classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
            renderValue={(items) => {
              if (items.length === 0) {
                return <span className="text-gray-400">{t("Tous les departements")}</span>;
              }
              return (
                <div className="flex flex-wrap gap-1">
                  {items.map((item) => (
                    <span
                      key={item.key}
                      className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded"
                    >
                      {item.textValue}
                    </span>
                  ))}
                </div>
              );
            }}
          >
            {departmentOptions.map((dept) => (
              <SelectItem
                key={dept.id}
                value={dept.id}
              >
                {dept.label}
              </SelectItem>
            ))}
          </Select>
          {rule.department_ids?.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {t("Aucun departement selectionne = tous les departements")}
            </p>
          )}
        </div>

        <Select
          label={t("Direction d'appel")}
          selectedKeys={[rule.call_direction || "all"]}
          onChange={(e) => e.target.value && onChange({ call_direction: e.target.value })}
          isDisabled={isReadOnly}
          variant={isReadOnly ? "flat" : "bordered"}
          classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
        >
          {CALL_DIRECTIONS.map((direction) => (
            <SelectItem
              key={direction.key}
              value={direction.key}
            >
              {t(direction.label)}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-700">{t("Composants de la note")}</div>
        <div className="p-3 bg-white border border-gray-200 rounded-lg min-h-[48px]">
          {(rule.components || []).length === 0 && (
            <span className="text-xs text-gray-400">{t("Aucun composant defini")}</span>
          )}
          <div className="space-y-2">
            {(rule.components || []).map((comp, idx) => {
              const fieldType = getFieldType(comp.field_name);
              const isBool = fieldType === "bool";

              return (
                <div
                  key={comp.field_name}
                  className="flex items-center gap-2 text-sm"
                >
                  <Chip
                    size="sm"
                    variant="flat"
                    color={fieldType === "number" ? "primary" : "success"}
                  >
                    {fieldType}
                  </Chip>
                  <span className="flex-1 font-medium">
                    {comp.field_name.replace(/_/g, " ")}
                    {comp.trigger_field && (
                      <Tooltip
                        content={`${t("Condition")}: ${comp.trigger_field.replace(/_/g, " ")}`}
                      >
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-600 cursor-default">
                          {t("si")} {comp.trigger_field.replace(/_/g, " ")}
                        </span>
                      </Tooltip>
                    )}
                  </span>
                  {isBool && (
                    <Tooltip
                      content={
                        comp.bool_inverted
                          ? t("Oui = 0/10, Non = 10/10")
                          : t("Oui = 10/10, Non = 0/10")
                      }
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (!isReadOnly) handleToggleBoolInverted(comp.field_name);
                        }}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                          comp.bool_inverted
                            ? "bg-amber-100 border-amber-300 text-amber-700"
                            : "bg-emerald-100 border-emerald-300 text-emerald-700"
                        } ${!isReadOnly ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                      >
                        {comp.bool_inverted ? `Oui = 0` : `Oui = 10`}
                      </button>
                    </Tooltip>
                  )}
                  <span className="text-gray-500 text-xs">
                    {t("poids")}: {comp.weight}
                  </span>
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div
                      className={`${WEIGHT_COLORS[idx % WEIGHT_COLORS.length]} h-2 rounded-full`}
                      style={{ width: `${(comp.weight / 10) * 100}%` }}
                    />
                  </div>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => handleRemoveComponent(comp.field_name)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                    >
                      <IoCloseSharp
                        size={14}
                        className="text-red-500"
                      />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <WeightDistributionBar components={rule.components || []} />
        </div>
        {errors.components && <p className="text-xs text-danger">{errors.components}</p>}

        {!isReadOnly && (
          <div className="space-y-2">
            <div className="flex gap-2 items-end">
              <Select
                label={t("Champ")}
                placeholder={t("Selectionner un champ")}
                selectedKeys={componentField ? [componentField] : []}
                onChange={(e) => setComponentField(e.target.value)}
                size="sm"
                className="flex-1"
                classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
              >
                {availableFields.map((field) => (
                  <SelectItem
                    key={field.name}
                    value={field.name}
                  >
                    {`${field.name.replace(/_/g, " ")} (${field.type})`}
                  </SelectItem>
                ))}
              </Select>
              <Input
                label={t("Poids")}
                type="number"
                min={1}
                max={10}
                value={componentWeight}
                onChange={(e) => setComponentWeight(e.target.value)}
                size="sm"
                className="w-24"
                classNames={{ input: "text-sm", label: "text-xs font-medium" }}
              />
              <Button
                size="sm"
                color="primary"
                variant="flat"
                onClick={handleAddComponent}
                isIconOnly
              >
                <LuPlus size={18} />
              </Button>
            </div>
            {triggerFieldOptions.length > 0 && (
              <Select
                label={t("Condition (optionnel)")}
                placeholder={t("Toujours evaluer")}
                selectedKeys={componentTrigger ? [componentTrigger] : []}
                onChange={(e) => setComponentTrigger(e.target.value)}
                size="sm"
                classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
              >
                {triggerFieldOptions.map((field) => (
                  <SelectItem
                    key={field.name}
                    value={field.name}
                  >
                    {`${t("Si")} ${field.name.replace(/_/g, " ")}`}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>
        )}
      </div>

      <GradeDemo
        components={rule.components || []}
        getFieldType={getFieldType}
        t={t}
      />
    </div>
  );
};

const GradingRulesPanel = ({
  apiUrl,
  organisations,
  isReadOnly = false,
  onDirtyChange = () => {},
  refreshKey = 0,
}) => {
  const { t } = useTranslation();

  const [gradingRules, setGradingRules] = useState([]);
  const [gradableFields, setGradableFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [expandedIndexes, setExpandedIndexes] = useState(() => new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [newRule, setNewRule] = useState(EMPTY_RULE);
  const [errors, setErrors] = useState({});
  const initialSnapshotRef = useRef("[]");

  const isDirty = JSON.stringify(gradingRules) !== initialSnapshotRef.current;

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty]);

  const departmentOptions = (organisations?.departments || []).map((dept) => ({
    key: dept.id,
    label: dept.name,
    id: dept.id,
  }));

  const getDepartmentNames = (departmentIds) => {
    if (!departmentIds || departmentIds.length === 0) return t("Tous les departements");
    const names = departmentIds
      .map((id) => {
        const department = organisations?.departments?.find((d) => d.id === id);
        return department?.name;
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : t("Departements inconnus");
  };

  const fetchGradingRules = async () => {
    setIsLoading(true);
    try {
      const response = await myAxios.get(`${apiUrl}/audio/api/v1/admin/get_company_grading_rules`);
      const data = response?.data?.[0] || {};
      const rules = data.grading_rules || [];
      setGradingRules(rules);
      setGradableFields(data.gradable_fields || []);
      initialSnapshotRef.current = JSON.stringify(rules);
    } catch (error) {
      console.error("Error fetching grading rules:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGradingRules();
  }, [refreshKey]);

  const getFieldType = (fieldName) => getFieldTypeFromList(gradableFields, fieldName);

  const validateRule = (rule) => {
    const errs = {};
    if (!rule.name?.trim()) errs.name = t("Le nom est requis");
    if (!rule.components || rule.components.length === 0) {
      errs.components = t("Au moins un composant est requis");
    }
    return errs;
  };

  const handleUpdateRule = (index, updates) => {
    const updated = [...gradingRules];
    updated[index] = { ...updated[index], ...updates };
    setGradingRules(updated);
  };

  const handleAddRule = () => {
    const ruleErrors = validateRule(newRule);
    if (Object.keys(ruleErrors).length > 0) {
      setErrors(ruleErrors);
      return;
    }

    setGradingRules([...gradingRules, { ...newRule }]);
    setNewRule(EMPTY_RULE);
    setErrors({});
    setIsCreating(false);
  };

  const handleDeleteRule = (index) => {
    setGradingRules(gradingRules.filter((_, idx) => idx !== index));
    setExpandedIndexes(new Set());
  };

  const handleCancel = () => {
    setGradingRules(JSON.parse(initialSnapshotRef.current));
    setErrors({});
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Priority = array index
      const rulesWithPriority = gradingRules.map((rule, idx) => ({
        ...rule,
        priority: idx,
      }));

      await myAxios.put(`${apiUrl}/audio/api/v1/admin/update_company_grading_rules`, {
        grading_rules: rulesWithPriority,
      });

      await fetchGradingRules();
    } catch (error) {
      console.error("Error saving grading rules:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpanded = (index) => {
    setExpandedIndexes((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <LuGauge
              size={20}
              className="text-primary"
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-[#171717]">{t("Regles de notation")}</h2>
              <Tooltip
                content={t(
                  "La note finale est calculee sur 10 a partir d'une moyenne ponderee des composants. La regle de plus haute priorite qui correspond au departement et a la direction de l'appel est appliquee."
                )}
              >
                <div className="cursor-pointer">
                  <IoInformationCircleOutline
                    size={18}
                    className="text-gray-400"
                  />
                </div>
              </Tooltip>
            </div>
            <p className="text-xs text-[#737373]">
              {gradingRules.length} {t(gradingRules.length > 1 ? "regles" : "regle")}
              {isReadOnly && ` · ${t("Lecture seule")}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <>
              <Button
                variant={isReorderMode ? "solid" : "flat"}
                color={isReorderMode ? "primary" : "default"}
                startContent={<HiSwitchVertical size={16} />}
                onClick={() => setIsReorderMode(!isReorderMode)}
                isDisabled={gradingRules.length < 2}
              >
                {isReorderMode ? t("Terminer") : t("Reorganiser les priorites")}
              </Button>
              <Button
                color="primary"
                startContent={<LuPlus size={16} />}
                onClick={() => setIsCreating(!isCreating)}
                isDisabled={isReorderMode}
              >
                {t("Ajouter une regle")}
              </Button>
            </>
          )}
        </div>
      </div>

      {isCreating && !isReadOnly && !isReorderMode && (
        <div className="mt-4 border-2 border-dashed border-primary/30 bg-primary/[0.02] rounded-xl p-4">
          <h3 className="text-sm font-semibold text-[#171717] mb-3 flex items-center gap-2">
            <LuPlus size={16} />
            {t("Ajouter une regle de notation")}
          </h3>
          {gradableFields.length === 0 && (
            <div className="text-xs text-amber-600 bg-amber-50 p-3 rounded-md mb-3">
              {t(
                "Aucun champ de type Note ou Oui/Non n'est disponible. Creez d'abord des champs d'analyse personnalises."
              )}
            </div>
          )}
          <RuleForm
            rule={newRule}
            onChange={(updates) => {
              setNewRule((previous) => ({ ...previous, ...updates }));
              const clearedErrors = { ...errors };
              Object.keys(updates).forEach((key) => {
                clearedErrors[key] = null;
              });
              setErrors(clearedErrors);
            }}
            isReadOnly={false}
            gradableFields={gradableFields}
            departmentOptions={departmentOptions}
            errors={errors}
            t={t}
          />
          <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-md mt-3">
            <span className="font-medium">{t("Formule")}: </span>
            <span className="italic">
              {t("Note = (somme des scores normalises x poids) / (somme des poids) x 10")}
            </span>
            <div className="mt-1 text-gray-400">
              {t("Note (0-10): valeur / 10 | Oui/Non: valeur selon configuration")}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="flat"
              onClick={() => {
                setIsCreating(false);
                setNewRule(EMPTY_RULE);
                setErrors({});
              }}
            >
              {t("Annuler")}
            </Button>
            <Button
              color="primary"
              startContent={<LuPlus size={16} />}
              onClick={handleAddRule}
              isDisabled={gradableFields.length === 0}
            >
              {t("Ajouter la regle")}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress />
          </div>
        ) : isReorderMode ? (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-4">
              {t(
                "Glissez-deposez pour reorganiser les priorites. La regle #1 est la plus prioritaire."
              )}
            </p>
            <Reorder.Group
              axis="y"
              values={gradingRules}
              onReorder={setGradingRules}
              className="space-y-2"
            >
              {gradingRules.map((rule, index) => (
                <ReorderableRuleCard
                  key={rule.name}
                  rule={rule}
                  index={index}
                  t={t}
                />
              ))}
            </Reorder.Group>
          </div>
        ) : (
          <div className="space-y-2">
            {gradingRules.length === 0 && (
              <div className="text-center py-12 text-gray-400 text-sm">
                {t("Aucune regle de notation definie")}
              </div>
            )}
            {gradingRules.map((rule, index) => {
              const isExpanded = expandedIndexes.has(index);
              return (
                <div
                  key={`${rule.name}-${index}`}
                  className={`border rounded-xl transition-colors ${
                    isExpanded ? "border-primary/40" : "border-[#E5E5E5]"
                  }`}
                >
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer select-none"
                    onClick={() => toggleExpanded(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") toggleExpanded(index);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <IoChevronDown
                      size={16}
                      className={`text-gray-400 shrink-0 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                    <Chip
                      size="sm"
                      color="primary"
                      variant="flat"
                    >
                      #{index + 1}
                    </Chip>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#171717] truncate">{rule.name}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {rule.department_ids?.length > 0 && (
                        <Tooltip content={getDepartmentNames(rule.department_ids)}>
                          <Chip
                            size="sm"
                            variant="flat"
                            color="warning"
                          >
                            {rule.department_ids.length} {t("dept(s)")}
                          </Chip>
                        </Tooltip>
                      )}
                      {rule.call_direction !== "all" && (
                        <Chip
                          size="sm"
                          variant="flat"
                          color="secondary"
                        >
                          {t(
                            CALL_DIRECTIONS.find((d) => d.key === rule.call_direction)?.label || ""
                          )}
                        </Chip>
                      )}
                      <Chip
                        size="sm"
                        variant="flat"
                      >
                        {rule.components?.length || 0} {t("composant(s)")}
                      </Chip>
                    </div>
                    {!isReadOnly && (
                      <div
                        className="flex items-center shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(index)}
                          title={t("Supprimer")}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <BsTrash
                            size={14}
                            className="text-red-500"
                          />
                        </button>
                      </div>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-[#F0F0F0]">
                      <RuleForm
                        rule={rule}
                        onChange={(updates) => handleUpdateRule(index, updates)}
                        isReadOnly={isReadOnly}
                        gradableFields={gradableFields}
                        departmentOptions={departmentOptions}
                        t={t}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isDirty && !isReadOnly && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-3 bg-[#171717] text-white rounded-xl px-4 py-3 shadow-lg">
          <span className="text-sm">{t("Unsaved changes")}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              className="bg-white/10 text-white"
              onClick={handleCancel}
              isDisabled={isSaving}
            >
              {t("Annuler")}
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t("Sauvegarder les modifications")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export { WeightDistributionBar };
export default GradingRulesPanel;
